import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    IconButton,
    Tooltip
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { getCollaborateurDocuments } from '../../../services/CollaborateurService';
import ShowAddDocCollaborateur from './ShowAddDocCollaborateur';

const CollaborateurDocuments = ({ collaborateurId, snackbarShowMessage, refreshKey, onDelete }) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [showDocModal, setShowDocModal] = useState(false);

    useEffect(() => {
        const fetchDocuments = async () => {
            try {
                const response = await getCollaborateurDocuments(collaborateurId);
                setDocuments(Array.isArray(response.data.data) ? response.data.data : []);
            } catch (error) {
                if (typeof snackbarShowMessage === 'function') {
                    snackbarShowMessage("Erreur chargement des documents du collaborateur.", "error");
                }
            } finally {
                setLoading(false);
            }
        };

        if (collaborateurId) fetchDocuments();
        else {
            setDocuments([]);
            setLoading(false);
        }
    }, [collaborateurId, snackbarShowMessage, refreshKey]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={30} sx={{ py: 1 }}>
                <CircularProgress size={18} thickness={4} color="primary" />
                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                    Chargement...
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.6,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        color: '#64748b',
                    }}
                >
                    <AttachFileIcon sx={{ fontSize: 15 }} />
                    Documents enregistrés
                </Typography>
                {documents.length > 0 && (
                    <Box sx={{ px: 0.9, borderRadius: '10px', bgcolor: '#ebf4fb', color: '#03407b', fontSize: '0.7rem', fontWeight: 800 }}>
                        {documents.length}
                    </Box>
                )}
            </Box>

            {documents.length === 0 ? (
                <Typography sx={{ fontSize: '0.76rem', color: '#94a3b8', fontStyle: 'italic' }}>
                    Aucun document enregistré pour le moment.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 260, overflowY: 'auto', pr: 0.25 }}>
                    {documents.map((doc, i) => (
                        <Box
                            key={doc.id || i}
                            onClick={() => {
                                setSelectedDoc(doc);
                                setShowDocModal(true);
                            }}
                            title="Voir le document"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1,
                                py: 0.75,
                                borderRadius: '8px',
                                bgcolor: '#fff',
                                border: '1px solid #e2e8f0',
                                cursor: 'pointer',
                                transition: 'all .15s',
                                '&:hover': { borderColor: '#03407b', bgcolor: '#f0f7fd' },
                            }}
                        >
                            <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: '#03407b' }} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography noWrap sx={{ fontSize: '0.76rem', fontWeight: 700, color: '#0f172a' }}>
                                    {doc.category || 'Non classé'}
                                </Typography>
                                <Typography noWrap sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                                    {doc.fileName || 'Nom du document introuvable'}
                                </Typography>
                            </Box>
                            {onDelete && (
                                <Tooltip title="Supprimer le document">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete(doc);
                                        }}
                                        sx={{ color: '#94a3b8', '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' } }}
                                    >
                                        <DeleteOutlinedIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    ))}
                </Box>
            )}
            {showDocModal && selectedDoc && (
                <ShowAddDocCollaborateur
                    open={showDocModal}
                    onClose={() => {
                        setShowDocModal(false);
                        setSelectedDoc(null);
                    }}
                    files={[selectedDoc]}
                    categorie={selectedDoc.category}
                    fileName={selectedDoc.fileName}
                    snackbarShowMessage={snackbarShowMessage}
                />
            )}
        </Box>
    );
};

export default CollaborateurDocuments;
